import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { useTranslation } from '../hooks/useTranslation';
import { divyaDesams, naduCategories } from '../data/divyaDesams';
import './DivyaDesams.css';

const DivyaDesams = () => {
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const hydrated = useHydrated();
  useTranslation();

  const naduLabels = ['All', ...naduCategories.map((n) => n.name)];

  const filteredTemples = divyaDesams.filter((temple) => {
    const matchesNadu = filter === 'All' || temple.nadu === filter;
    const matchesSearch =
      temple.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      temple.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      temple.deity.toLowerCase().includes(searchTerm.toLowerCase()) ||
      temple.district.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesNadu && matchesSearch;
  });

  const handleNaduClick = (naduName) => {
    setFilter(naduName);
    const listingSection = document.getElementById('divya-desams-listing');
    if (listingSection) {
      listingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="divya-desams-page">
      <SEO
        title="108 Divya Desams - Complete Guide to Sacred Vishnu Temples | Parithi"
        description="Explore all 108 Divya Desams, the sacred Vishnu temples glorified by the 12 Azhwar saints in Divya Prabandham. Complete guide with details of temples across Chola Nadu, Pandya Nadu, Malai Nadu, Thondai Nadu, and Vada Nadu."
        keywords="108 divya desams, divya desam temples, vishnu temples india, azhwar saints, divya prabandham, sri vaishnava temples, chola nadu temples, pandya nadu temples"
        canonicalPath="/108-divya-desams"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "108 Divya Desams - Complete Guide to Sacred Vishnu Temples",
            "description": "Explore all 108 Divya Desams, the sacred Vishnu temples glorified by the 12 Azhwar saints in Divya Prabandham. Complete guide with details of temples across Chola Nadu, Pandya Nadu, Malai Nadu, Thondai Nadu, and Vada Nadu.",
            "url": "https://www.parithi.com/108-divya-desams",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What are the 108 Divya Desams?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The 108 Divya Desams are sacred Vishnu temples that were glorified by the 12 Azhwar saints in their collection of 4,000 Tamil hymns known as the Nalayira Divya Prabandham. These temples are spread across India and one in Nepal, with the majority located in Tamil Nadu, Kerala, and Andhra Pradesh. They hold the highest spiritual significance in the Sri Vaishnava tradition.",
                },
              },
              {
                "@type": "Question",
                "name": "Who are the 12 Azhwar saints?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The 12 Azhwars are Tamil poet-saints devoted to Lord Vishnu who lived between the 6th and 9th centuries CE. They are Poigai Azhwar, Bhuthathazhwar, Peyazhwar, Thirumazhisai Azhwar, Nammazhwar, Madhurakavi Azhwar, Kulasekara Azhwar, Periyazhwar, Andal, Thondaradippodi Azhwar, Thiruppaan Azhwar, and Thirumangai Azhwar. Their hymns form the Divya Prabandham, the cornerstone of Sri Vaishnava devotional literature.",
                },
              },
              {
                "@type": "Question",
                "name": "How are the 108 Divya Desams classified by region?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The 108 Divya Desams are traditionally classified into six Nadu (regional) categories: Chola Nadu (40 temples in the Kaveri delta region), Pandya Nadu (18 temples in southern Tamil Nadu), Thondai Nadu (22 temples around Kanchipuram and Chennai), Malai Nadu (13 temples in Kerala), Nadu Nadu (2 temples in central Tamil Nadu), and Vada Nadu (13 temples in North India, Nepal, and two celestial abodes).",
                },
              },
              {
                "@type": "Question",
                "name": "What is the Divya Prabandham?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Nalayira Divya Prabandham is a collection of 4,000 Tamil verses (pasurams) composed by the 12 Azhwar saints in praise of Lord Vishnu and His various forms at the 108 Divya Desams. Compiled by Nathamuni in the 10th century, it is considered equivalent to the Vedas in the Sri Vaishnava tradition and is recited daily in all Divya Desam temples. The Prabandham encompasses diverse poetic forms including devotional hymns, philosophical discourses, and bridal mysticism.",
                },
              },
            ],
          },
        ]}
      />

      <div className="container">
        {/* Hero Section */}
        <motion.div
          className="divya-desams-hero"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>
            108 <span className="gradient-text">Divya Desams</span>
          </h1>
          <p className="divya-desams-subtitle">
            The sacred Vishnu temples glorified by the 12 Azhwar saints in the Nalayira Divya Prabandham — a complete guide to every temple across India and Nepal.
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          className="divya-desams-stats"
          initial={hydrated ? { opacity: 0, scale: 0.95 } : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
        >
          <div className="divya-desams-stat">
            <span className="divya-desams-stat-number">108</span>
            <span className="divya-desams-stat-label">Temples</span>
          </div>
          <div className="divya-desams-stat">
            <span className="divya-desams-stat-number">6</span>
            <span className="divya-desams-stat-label">Nadus</span>
          </div>
          <div className="divya-desams-stat">
            <span className="divya-desams-stat-number">5</span>
            <span className="divya-desams-stat-label">States</span>
          </div>
          <div className="divya-desams-stat">
            <span className="divya-desams-stat-number">12</span>
            <span className="divya-desams-stat-label">Azhwars</span>
          </div>
        </motion.div>

        {/* Nadu Category Cards */}
        <motion.div
          className="divya-desams-nadu-grid"
          initial={hydrated ? { opacity: 0, y: 20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          {naduCategories.map((nadu, idx) => (
            <motion.div
              key={nadu.slug}
              className="divya-desams-nadu-card"
              style={{ backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.75)), url(${nadu.image})` }}
              initial={hydrated ? { opacity: 0, y: 20 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * idx + 0.3 }}
              onClick={() => handleNaduClick(nadu.name)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNaduClick(nadu.name); }}
            >
              <div className="divya-desams-nadu-card-content">
                <h3>{nadu.name}</h3>
                <p>{nadu.description}</p>
                <span className="divya-desams-nadu-count">{nadu.templeCount} Temples</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Search and Filter */}
        <div id="divya-desams-listing">
          <motion.div
            className="divya-desams-controls"
            initial={hydrated ? { opacity: 0, y: 10 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="divya-desams-search">
              <input
                type="text"
                placeholder="Search by temple name, city, deity, or district..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="divya-desams-search-input"
              />
            </div>
            <div className="divya-desams-filter-tags">
              {naduLabels.map((nadu) => (
                <button
                  key={nadu}
                  className={`divya-desams-filter-tag ${filter === nadu ? 'active' : ''}`}
                  onClick={() => setFilter(nadu)}
                >
                  {nadu}
                </button>
              ))}
            </div>
            <div className="divya-desams-count">
              Showing {filteredTemples.length} of 108 temples
            </div>
          </motion.div>
        </div>

        {/* Temple Listing Grid */}
        <div className="divya-desams-grid">
          {filteredTemples.map((temple, idx) => (
            <motion.div
              key={temple.id}
              className="divya-desams-temple-card"
              initial={hydrated ? { opacity: 0, y: 20 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(0.05 * idx, 1) }}
            >
              <div className="divya-desams-temple-card-body">
                <h3>{temple.name}</h3>
                <div className="divya-desams-temple-meta">
                  <span className="divya-desams-temple-location">
                    {temple.city}, {temple.district}, {temple.state}
                  </span>
                  <span className="divya-desams-temple-deity">
                    {temple.deity}
                  </span>
                </div>
                <p className="divya-desams-temple-brief">{temple.brief}</p>
                <Link to={`/108-divya-desams/${temple.slug}`} className="divya-desams-temple-link">
                  View Details
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredTemples.length === 0 && (
          <div className="divya-desams-no-results">
            <p>No temples found matching your search. Try a different keyword or clear filters.</p>
          </div>
        )}

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>108 Divya Desams — Complete Guide to the Sacred Vishnu Temples</h2>
          <p>
            The 108 Divya Desams are the holiest Vishnu temples in the Sri Vaishnava tradition, each one glorified by the 12 Azhwar poet-saints in the Nalayira Divya Prabandham — a revered collection of 4,000 Tamil verses composed between the 6th and 9th centuries CE. These temples span the Indian subcontinent, from the Kaveri delta in Tamil Nadu to the Himalayan foothills in Nepal, with two celestial abodes (Thiruparkadal and Paramapadam) beyond the earthly realm. For centuries, pilgrims have undertaken the sacred journey of visiting all 108 Divya Desams, a tradition known as "Divya Desa Yatra," believed to bestow liberation (moksha).
          </p>
          <p>
            The twelve Azhwar saints — Poigai Azhwar, Bhuthathazhwar, Peyazhwar, Thirumazhisai Azhwar, Nammazhwar, Madhurakavi Azhwar, Kulasekara Azhwar, Periyazhwar, Andal, Thondaradippodi Azhwar, Thiruppaan Azhwar, and Thirumangai Azhwar — were deeply immersed in devotion to Sriman Narayana. Their hymns, later compiled by Nathamuni in the 10th century, form the devotional backbone of Sri Vaishnavism and are recited daily in every Divya Desam temple. Srirangam, the foremost Divya Desam, is the only temple praised by all twelve Azhwars.
          </p>
          <p>
            The temples are traditionally classified into six regional groups called Nadus. Chola Nadu in the fertile Kaveri delta holds the largest share with 40 temples, including Srirangam and the famous Thirunangur cluster of 11 temples. Thondai Nadu around Kanchipuram and Chennai has 22 temples, while Pandya Nadu in southern Tamil Nadu has 18. Kerala's Malai Nadu contains 13 temples known for their unique architectural style. Vada Nadu encompasses 13 temples across North India, including Badrinath and the two metaphysical abodes. Nadu Nadu, the smallest grouping, has two temples in central Tamil Nadu.
          </p>
          <p>
            The Divya Prabandham, often called the "Tamil Veda," holds equal standing with the Sanskrit Vedas in the Sri Vaishnava tradition established by the great acharyas Ramanuja, Vedanta Desika, and Manavala Mamunigal. Each temple has its own unique presiding deity form, associated legends, and Prabandham pasurams (verses), making every Divya Desam a distinct spiritual experience. The tradition continues to thrive today, with millions of devotees visiting these temples annually, especially during festivals like Vaikunta Ekadasi, Panguni Uthiram, and Brahmotsavam.
          </p>

          <h3>Frequently Asked Questions About 108 Divya Desams</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>What are the 108 Divya Desams?</h4>
              <p>The 108 Divya Desams are sacred Vishnu temples that were glorified by the 12 Azhwar saints in their collection of 4,000 Tamil hymns known as the Nalayira Divya Prabandham. These temples are spread across India and one in Nepal, with the majority located in Tamil Nadu, Kerala, and Andhra Pradesh. They hold the highest spiritual significance in the Sri Vaishnava tradition.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Who are the 12 Azhwar saints?</h4>
              <p>The 12 Azhwars are Tamil poet-saints devoted to Lord Vishnu who lived between the 6th and 9th centuries CE. They are Poigai Azhwar, Bhuthathazhwar, Peyazhwar, Thirumazhisai Azhwar, Nammazhwar, Madhurakavi Azhwar, Kulasekara Azhwar, Periyazhwar, Andal, Thondaradippodi Azhwar, Thiruppaan Azhwar, and Thirumangai Azhwar. Their hymns form the Divya Prabandham, the cornerstone of Sri Vaishnava devotional literature.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How are the 108 Divya Desams classified by region?</h4>
              <p>The 108 Divya Desams are traditionally classified into six Nadu (regional) categories: Chola Nadu (40 temples in the Kaveri delta region), Pandya Nadu (18 temples in southern Tamil Nadu), Thondai Nadu (22 temples around Kanchipuram and Chennai), Malai Nadu (13 temples in Kerala), Nadu Nadu (2 temples in central Tamil Nadu), and Vada Nadu (13 temples in North India, Nepal, and two celestial abodes).</p>
            </div>
            <div className="faq-seo-item">
              <h4>What is the Divya Prabandham?</h4>
              <p>The Nalayira Divya Prabandham is a collection of 4,000 Tamil verses (pasurams) composed by the 12 Azhwar saints in praise of Lord Vishnu and His various forms at the 108 Divya Desams. Compiled by Nathamuni in the 10th century, it is considered equivalent to the Vedas in the Sri Vaishnava tradition and is recited daily in all Divya Desam temples. The Prabandham encompasses diverse poetic forms including devotional hymns, philosophical discourses, and bridal mysticism.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DivyaDesams;
